import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Lock, CheckCircle2, Info } from "lucide-react";
import { TEAMS, POOL_SIZES, POOLS, SCHEDULE } from "../data/tournament";
import { api } from "../lib/api";
import { computeStandings } from "../lib/standings";

// Team id → display name (mirrors SCHEDULE which uses display names for away/home)
const TEAM_NAME_BY_ID = TEAMS.reduce((m, t) => { m[t.id] = t.name; return m; }, {});

const StandingsTable = ({ title, subtitle, standings, accent = "bg-[#E60012]", highlightTop = 0 }) => (
  <div className="bg-[#111] border border-white/10 overflow-hidden">
    <div className={`${accent} px-5 py-3 flex items-center justify-between`}>
      <div className="font-display text-2xl uppercase">{title}</div>
      {subtitle && (
        <span className="font-condensed uppercase tracking-[0.25em] text-xs text-white/90">{subtitle}</span>
      )}
    </div>

    {/* Desktop / tablet */}
    <div className="hidden sm:block">
      <div className="grid grid-cols-[40px_1fr_50px_50px_50px_50px_60px_60px_70px_60px] gap-2 px-5 py-3 bg-[#0c0c0c] font-condensed uppercase tracking-[0.18em] text-[10px] text-white/60">
        <div>#</div>
        <div>Équipe</div>
        <div className="text-center">PJ</div>
        <div className="text-center">V</div>
        <div className="text-center">D</div>
        <div className="text-center">N</div>
        <div className="text-center">BP</div>
        <div className="text-center">BC</div>
        <div className="text-center" title="Différentiel plafonné à ±7 par partie">DIFF</div>
        <div className="text-center">PTS</div>
      </div>
      {standings.map((row, idx) => {
        const diff = row.diffCapped ?? (row.gf - row.ga);
        const isTop = highlightTop > 0 && idx < highlightTop;
        return (
          <div
            key={row.team}
            className={`grid grid-cols-[40px_1fr_50px_50px_50px_50px_60px_60px_70px_60px] gap-2 px-5 py-4 items-center border-t border-white/5 text-sm ${
              isTop ? "bg-[#E60012]/10" : idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"
            }`}
            data-testid={`standings-row-${row.team.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className={`font-display text-xl ${isTop ? "text-[#E60012]" : "text-white/80"}`}>
              {idx + 1}
            </div>
            <div className="font-condensed truncate">{row.team}</div>
            <div className="text-center text-white/70">{row.pj || "—"}</div>
            <div className="text-center text-white/70">{row.v || "—"}</div>
            <div className="text-center text-white/70">{row.d || "—"}</div>
            <div className="text-center text-white/70">{row.n || "—"}</div>
            <div className="text-center text-white/80 font-condensed">{row.pj ? row.gf : "—"}</div>
            <div className="text-center text-white/80 font-condensed">{row.pj ? row.ga : "—"}</div>
            <div className={`text-center font-condensed ${diff > 0 ? "text-emerald-400" : diff < 0 ? "text-red-400" : "text-white/60"}`}>
              {row.pj ? (diff > 0 ? `+${diff}` : diff) : "—"}
            </div>
            <div className="text-center text-white font-display text-lg">{row.pts || "—"}</div>
          </div>
        );
      })}
    </div>

    {/* Mobile cards */}
    <div className="sm:hidden">
      {standings.map((row, idx) => {
        const diff = row.diffCapped ?? (row.gf - row.ga);
        const isTop = highlightTop > 0 && idx < highlightTop;
        return (
          <div
            key={row.team}
            className={`px-4 py-4 border-t border-white/5 ${
              isTop ? "bg-[#E60012]/10" : idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"
            }`}
            data-testid={`standings-mobile-${row.team.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="flex items-center gap-3">
              <div className={`font-display text-2xl w-8 ${isTop ? "text-[#E60012]" : "text-white/80"}`}>
                {idx + 1}
              </div>
              <div className="font-condensed flex-1 truncate">{row.team}</div>
              <div className="font-display text-2xl text-white">{row.pts || "—"}</div>
              <div className="font-condensed uppercase tracking-[0.15em] text-[10px] text-white/50">PTS</div>
            </div>
            <div className="mt-3 grid grid-cols-7 gap-1 text-center">
              {[
                ["PJ", row.pj],
                ["V", row.v],
                ["D", row.d],
                ["N", row.n],
                ["BP", row.pj ? row.gf : "—"],
                ["BC", row.pj ? row.ga : "—"],
                ["DIFF", row.pj ? (diff > 0 ? `+${diff}` : diff) : "—"],
              ].map(([label, val]) => (
                <div key={label} className="bg-black/40 py-1.5">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-white/50 font-condensed">{label}</div>
                  <div className="text-xs text-white">{val || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Classement = () => {
  const [scores, setScores] = useState([]);
  const [pimRows, setPimRows] = useState([]);

  useEffect(() => {
    api.get("/scores").then((r) => setScores(r.data)).catch(() => setScores([]));
    api.get("/team-pim-by-game").then((r) => setPimRows(r.data || [])).catch(() => setPimRows([]));
  }, []);

  const scoreByGame = useMemo(() => {
    const m = {};
    scores.forEach((s) => (m[s.game_num] = s));
    return m;
  }, [scores]);

  // Aggregate PIM per team name, restricted to preliminary games (exclude phase != undefined)
  const pimByTeam = useMemo(() => {
    const prelimNums = new Set(SCHEDULE.filter((g) => !g.phase).map((g) => g.num));
    const out = {};
    pimRows.forEach((r) => {
      if (!prelimNums.has(r.game_num)) return;
      const name = TEAM_NAME_BY_ID[r.team_id];
      if (!name) return;
      out[name] = (out[name] || 0) + (r.pim || 0);
    });
    return out;
  }, [pimRows]);

  const categoryView = useMemo(() => {
    return ["2017", "2018"].map((cat) => {
      const teams = TEAMS.filter((t) => t.category === cat).map((t) => t.name);
      const standings = computeStandings(scoreByGame, teams, pimByTeam);
      const prelim = SCHEDULE.filter((g) => g.category === cat && !g.phase);
      const playedFinal = prelim.filter((g) => scoreByGame[g.num]?.status === "final").length;
      const totalPrelim = prelim.length;
      const allDone = totalPrelim > 0 && playedFinal === totalPrelim;

      let pools = null;
      if (allDone) {
        const sizeA = POOL_SIZES[cat]?.A || (POOLS[cat]?.A || []).length || Math.ceil(standings.length / 2);
        const sizeB = POOL_SIZES[cat]?.B || (POOLS[cat]?.B || []).length || standings.length - sizeA;
        pools = {
          A: standings.slice(0, sizeA),
          B: standings.slice(sizeA, sizeA + sizeB),
        };
      }
      return { cat, standings, allDone, playedFinal, totalPrelim, pools };
    });
  }, [scoreByGame, pimByTeam]);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="classement-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-8 sm:py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            En direct du tournoi
          </span>
          <h1 className="mt-2 sm:mt-3 font-display text-4xl sm:text-6xl lg:text-8xl uppercase leading-none">
            <span className="text-[#E60012]">Classement</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl text-sm sm:text-base">
            Pendant la phase préliminaire, toutes les équipes d'une même catégorie sont classées
            ensemble. Les <span className="text-white/85">Pools A et B</span> seront révélés une fois
            les 4 parties préliminaires terminées par toutes les équipes.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-16 space-y-12 sm:space-y-16">
        {categoryView.map(({ cat, standings, allDone, playedFinal, totalPrelim, pools }, i) => (
          <motion.div
            key={cat}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            data-testid={`category-${cat}`}
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-[#E60012]" />
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase">Catégorie {cat}</h2>
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-condensed uppercase tracking-[0.18em] text-[10px] sm:text-xs text-white/55">
                {playedFinal}/{totalPrelim} parties
              </span>
            </div>

            {/* Combined standings (always visible during prelim) */}
            <StandingsTable
              title={allDone ? "Classement final préliminaire" : "Phase préliminaire"}
              subtitle={`Catégorie ${cat}`}
              standings={standings}
              highlightTop={allDone ? POOL_SIZES[cat]?.A || 0 : 0}
            />

            {/* Pools (only after all preliminary games are final) */}
            {allDone && pools && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-4 text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-condensed uppercase tracking-[0.25em] text-xs">
                    Pools déterminés — Phase finale
                  </span>
                </div>
                <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                  <StandingsTable
                    title="Pool A"
                    subtitle={`Catégorie ${cat}`}
                    standings={pools.A}
                    accent="bg-[#E60012]"
                    highlightTop={1}
                  />
                  <StandingsTable
                    title="Pool B"
                    subtitle={`Catégorie ${cat}`}
                    standings={pools.B}
                    accent="bg-zinc-700"
                    highlightTop={0}
                  />
                </div>
              </div>
            )}

            {/* Lock state hint when not done yet */}
            {!allDone && (
              <div className="mt-4 px-4 py-3 bg-black/40 border border-white/10 flex items-center gap-3 text-sm text-white/60">
                <Lock className="w-4 h-4 text-[#E60012] shrink-0" />
                <span>
                  Les Pools <span className="text-white/85">A</span> et{" "}
                  <span className="text-white/85">B</span> seront dévoilés à la fin des parties préliminaires.
                </span>
              </div>
            )}
          </motion.div>
        ))}

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-[#111] border border-white/10 p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-[#E60012]" />
              <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#E60012]">
                Légende des colonnes
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-white/75">
              <div><span className="font-display text-white">PJ</span> · Parties jouées</div>
              <div><span className="font-display text-white">V</span> · Victoires</div>
              <div><span className="font-display text-white">D</span> · Défaites</div>
              <div><span className="font-display text-white">N</span> · Nulles</div>
              <div><span className="font-display text-white">BP</span> · Buts pour</div>
              <div><span className="font-display text-white">BC</span> · Buts contre</div>
              <div className="col-span-2"><span className="font-display text-white">DIFF</span> · Différentiel (BP − BC), plafonné à ±7 par partie</div>
              <div className="col-span-2"><span className="font-display text-white">PTS</span> · Points (V = 2 · N = 1 · D = 0)</div>
            </dl>
          </div>
          <div className="bg-[#111] border border-white/10 p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-[#E60012]" />
              <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#E60012]">
                Bris d'égalité (dans l'ordre)
              </div>
            </div>
            <ol className="list-decimal list-inside space-y-1 text-sm text-white/75 marker:text-[#E60012] marker:font-display">
              <li>Plus grand nombre de victoires</li>
              <li>Moins de défaites</li>
              <li>Moins de buts contre</li>
              <li>Résultat face-à-face <span className="text-white/50">(si les équipes se sont affrontées)</span></li>
              <li>Différentiel (plafonné ±7 par partie)</li>
              <li>Plus de buts pour</li>
              <li>Moins de minutes de pénalités en préliminaires</li>
              <li>Tirage au sort</li>
            </ol>
            <p className="mt-3 text-xs text-white/50">
              Si 3 équipes ou plus sont à égalité et ne se sont pas toutes affrontées, les étapes 4 et 7 sont exclues.
            </p>
          </div>
        </div>

        <div className="bg-[#111] border border-white/10 p-6 sm:p-8 text-center">
          <div className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012] mb-2">
            Note
          </div>
          <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto">
            Le classement se met à jour automatiquement à mesure que les organisateurs marquent les
            matchs comme « Final » dans le panneau d'administration.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Classement;
